/**
 * Scheduling-mode constants, discriminators and date labels shared by admin
 * authoring, the guest booking surfaces, the cart, and booking and request
 * readers. Mirrors the `scheduling_mode` strings the backend returns.
 */
import type {
  Booking,
  BookingRequest,
  SchedulingMode,
  Service,
  ServiceBookableWindows,
  ServiceOccurrence,
} from '../types';
import type { CalendarWindow } from './calendarWindow';
import { formatCalendarDate, formatCheckTime } from './formatters';
import { isServiceDateAvailable } from './serviceBookingInputs';

export const SCHEDULING_MODE_GUEST_PICKS: SchedulingMode = 'GUEST_PICKS';
export const SCHEDULING_MODE_UNDATED: SchedulingMode = 'UNDATED';
export const SCHEDULING_MODE_FIXED_SCHEDULE: SchedulingMode = 'FIXED_SCHEDULE';

/** True for the modes whose options cannot be priced per hour, 30 minutes or day: undated and fixed-schedule. */
export const schedulingModeForbidsTimeUnits = (mode: SchedulingMode): boolean =>
  mode === SCHEDULING_MODE_UNDATED || mode === SCHEDULING_MODE_FIXED_SCHEDULE;

/** The single muted line shown in place of the date and time controls on an undated service. */
export const UNDATED_SERVICE_NOTE = 'No set date — use any time';

/** True when the service is booked with no date or time. A service without the field is guest-picks. */
export const isUndatedService = (service: Service): boolean =>
  service.scheduling_mode === SCHEDULING_MODE_UNDATED;

/** The single label shown wherever an undated booking would otherwise print a date. */
export const UNDATED_DATE_LABEL = 'No set date';

/** True when the booking has no date or time. A booking without the field is dated. */
export const isUndatedBooking = (booking: Pick<Booking, 'scheduling_mode'>): boolean =>
  booking.scheduling_mode === SCHEDULING_MODE_UNDATED;

/** True when the booking is for one of a fixed-schedule service's occurrences. */
export const isFixedBooking = (booking: Pick<Booking, 'scheduling_mode'>): boolean =>
  booking.scheduling_mode === SCHEDULING_MODE_FIXED_SCHEDULE;

export interface RequestDateFormatting {
  formatDate: (isoDate: string) => string;
  separator: string;
}

const storedTime = (hhmm: string): string => hhmm;

/** The times of a schedule as one phrase, or null when it has none: a range,
 *  or "From" / "Until" when only one end is set. */
export const formatTimeRange = (
  startTime: string | null | undefined,
  endTime: string | null | undefined,
  formatTime: (hhmm: string) => string = storedTime,
): string | null => {
  if (startTime && endTime) return `${formatTime(startTime)} \u2013 ${formatTime(endTime)}`;
  if (startTime) return `From ${formatTime(startTime)}`;
  if (endTime) return `Until ${formatTime(endTime)}`;
  return null;
};

/** The dates line of a booking request: a single date, a range, or the
 *  undated label, followed by its times when it has any. */
export const formatRequestDates = (
  request: Pick<BookingRequest, 'start_date' | 'end_date' | 'start_time' | 'end_time'>,
  formatting: RequestDateFormatting,
): string => {
  if (request.start_date === null) return UNDATED_DATE_LABEL;
  const start = formatting.formatDate(request.start_date);
  const dates = request.end_date
    ? `${start}${formatting.separator}${formatting.formatDate(request.end_date)}`
    : start;
  const times = formatTimeRange(request.start_time, request.end_time);
  return times ? `${dates}, ${times}` : dates;
};

/** The dates line of a fixed-schedule booking: its range (one date when it
 *  starts and ends the same day) followed by its times. */
export const formatFixedBookingDates = (
  booking: Pick<
    Booking,
    'start_date' | 'end_date' | 'service_date' | 'service_time' | 'service_end_time'
  >,
  formatting: RequestDateFormatting,
): string => {
  const startDate = booking.service_date ?? booking.start_date;
  return formatRequestDates(
    {
      start_date: startDate,
      end_date: booking.end_date === startDate ? null : booking.end_date,
      start_time: booking.service_time,
      end_time: booking.service_end_time,
    },
    formatting,
  );
};

/** True when the service is booked on one of its authored occurrences. */
export const isFixedScheduleService = (service: Service): boolean =>
  service.scheduling_mode === SCHEDULING_MODE_FIXED_SCHEDULE;

/** Shown in place of the occurrence choice when none is bookable. */
export const NO_UPCOMING_DATES_LABEL = 'No upcoming dates';

export interface OccurrenceSchedule {
  /** The date or date range line. */
  primary: string;
  /** The time line, or null when the occurrence has no time. */
  secondary: string | null;
}

/** The two display lines of an occurrence: its dates, and its times as stored
 *  unless `formatTime` renders them another way. */
export const formatOccurrenceSchedule = (
  occurrence: ServiceOccurrence,
  formatTime: (hhmm: string) => string = storedTime,
): OccurrenceSchedule => {
  const { start_date, end_date, start_time, end_time } = occurrence;
  const weekdayDate: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  };
  const year: Intl.DateTimeFormatOptions = { year: 'numeric' };
  const start = formatCalendarDate(start_date, weekdayDate) ?? start_date;
  const startYear = formatCalendarDate(start_date, year) ?? '';
  let primary = `${start}, ${startYear}`;
  if (end_date) {
    const end = formatCalendarDate(end_date, weekdayDate) ?? end_date;
    const endYear = formatCalendarDate(end_date, year) ?? '';
    primary =
      endYear === startYear
        ? `${start} \u2013 ${end}, ${endYear}`
        : `${start}, ${startYear} \u2013 ${end}, ${endYear}`;
  }
  const secondary = formatTimeRange(start_time, end_time, formatTime);
  return { primary, secondary };
};

/** The schedule of an occurrence on one line, for the cart and checkout. */
export const formatOccurrenceLine = (occurrence: ServiceOccurrence): string => {
  const { primary, secondary } = formatOccurrenceSchedule(occurrence);
  return secondary ? `${primary} \u00b7 ${secondary}` : primary;
};

/** 12-hour rendering of a stored `HH:MM` time, falling back to the stored text. */
export const formatSheetTime = (hhmm: string): string => formatCheckTime(hhmm) ?? hhmm;

/** One-line summary of an occurrence for the mobile sheet and bar: the date
 *  with a 12-hour start time for a single day, the date range alone otherwise. */
export const formatOccurrenceShortLabel = (occurrence: ServiceOccurrence): string => {
  const { start_date, end_date, start_time } = occurrence;
  const weekdayDate: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  };
  const start = formatCalendarDate(start_date, weekdayDate) ?? start_date;
  if (end_date) {
    return `${start} \u2013 ${formatCalendarDate(end_date, weekdayDate) ?? end_date}`;
  }
  return start_time ? `${start} \u00b7 ${formatSheetTime(start_time)}` : start;
};

/** The read of a service with no booking windows: every date is allowed. Also the fail-open value. */
export const NOT_WINDOWED: ServiceBookableWindows = { is_windowed: false, windows: [] };

/** Defence-in-depth error for a date outside every booking window. */
export const OUTSIDE_BOOKABLE_WINDOWS_MESSAGE =
  'This date is outside the dates this service can be booked.';

/** True when the date lies in any window, both ends inclusive. An unwindowed read allows every date. */
export const isInsideBookableWindows = (
  bookableWindows: ServiceBookableWindows,
  dateIso: string,
): boolean =>
  !bookableWindows.is_windowed ||
  bookableWindows.windows.some(
    (window) => window.start_date <= dateIso && dateIso <= window.end_date,
  );

/** False for a windowed service with no window ending on or after the guest-local today. */
export const hasUpcomingWindowDay = (
  bookableWindows: ServiceBookableWindows,
  todayIso: string,
): boolean =>
  !bookableWindows.is_windowed ||
  bookableWindows.windows.some((window) => window.end_date >= todayIso);

/** Narrows the calendar to the windows: it opens at the later of the guest-local today and the
 *  first window start, and ends at the last window end (replacing the usual horizon). */
export const boundCalendarToWindows = (
  calendarWindow: CalendarWindow,
  bookableWindows: ServiceBookableWindows,
): CalendarWindow => {
  if (!bookableWindows.is_windowed || bookableWindows.windows.length === 0) return calendarWindow;
  const starts = bookableWindows.windows.map((window) => window.start_date);
  const ends = bookableWindows.windows.map((window) => window.end_date);
  const earliestStart = starts.reduce((earliest, start) => (start < earliest ? start : earliest));
  const latestEnd = ends.reduce((latest, end) => (end > latest ? end : latest));
  return {
    minIso: earliestStart > calendarWindow.minIso ? earliestStart : calendarWindow.minIso,
    maxIso: latestEnd,
  };
};

/** True when the weekday rule or the booking windows rule out the date. */
export const isServiceDateUnavailable = (
  availableDays: string[] | null | undefined,
  bookableWindows: ServiceBookableWindows,
  dateIso: string,
): boolean =>
  !isServiceDateAvailable(availableDays, dateIso) ||
  !isInsideBookableWindows(bookableWindows, dateIso);

/** One muted line listing the windows that have not ended by the guest-local today, or null. */
export const formatBookableWindowsSummary = (
  bookableWindows: ServiceBookableWindows,
  todayIso: string,
): string | null => {
  if (!bookableWindows.is_windowed) return null;
  const monthDay: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  const year: Intl.DateTimeFormatOptions = { year: 'numeric' };
  const upcoming = bookableWindows.windows.filter((window) => window.end_date >= todayIso);
  if (upcoming.length === 0) return null;
  const ranges = upcoming.map(({ start_date, end_date }) => {
    const start = formatCalendarDate(start_date, monthDay) ?? start_date;
    const startYear = formatCalendarDate(start_date, year) ?? '';
    if (end_date === start_date) return `${start}, ${startYear}`;
    const end = formatCalendarDate(end_date, monthDay) ?? end_date;
    const endYear = formatCalendarDate(end_date, year) ?? '';
    return endYear === startYear
      ? `${start} \u2013 ${end}, ${endYear}`
      : `${start}, ${startYear} \u2013 ${end}, ${endYear}`;
  });
  return `Available ${ranges.join(' \u00b7 ')}`;
};
